import { z } from 'zod';
import { DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE, SORT_ORDERS } from './list-query.schemas';
import type { Currency } from '../money/money';

/**
 * What repeat repairs and warranty cases cost, and the trend of a part's cost on a truck (gitaumoses4/chukua#175).
 * Both reports read the same derived flags as the work order list and never add one currency to another: every row
 * and every point belongs to one currency.
 */

/** The longest span either report reads, in months, so a trend stays a chart and not an export. */
export const REPAIR_COST_MAX_MONTHS = 24;

export const REPAIR_COST_GROUPINGS = ['truck', 'garage', 'partType'] as const;
export type RepairCostGrouping = (typeof REPAIR_COST_GROUPINGS)[number];

export const FLAG_REPORT_SORT_FIELDS = ['flaggedCost', 'flaggedOrders', 'unflaggedCost', 'totalCost'] as const;

const date = z.iso.date();
const month = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Use YYYY-MM');

/** `GET /repair-cost/flag-report` — `from` and `to` are fit dates (inclusive, in the company's time zone). */
export const repairCostFlagReportQuerySchema = z.object({
  groupBy: z.enum(REPAIR_COST_GROUPINGS).default('truck'),
  from: date.optional(),
  to: date.optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
  sort: z.enum(FLAG_REPORT_SORT_FIELDS).default('flaggedCost'),
  order: z.enum(SORT_ORDERS).default('desc'),
});
export type RepairCostFlagReportQuery = z.infer<typeof repairCostFlagReportQuerySchema>;

export interface BearerCostsView {
  owner: number;
  garage: number;
  absorbed: number;
  undecided: number;
}

export interface ClaimOutcomesView {
  /** Claims raised or submitted and not yet answered. */
  open: number;
  accepted: number;
  rejected: number;
  withdrawn: number;
  /** What accepted claims recovered, minor units, in the row's currency. */
  recoveredMinor: number;
}

/**
 * One group in one currency. Costs are minor units: labour plus parts for a truck or a garage, parts alone for a part
 * type (labour is not recorded per part). Grouped by part type an order that fitted two types is in both rows, so those
 * rows overlap and should not be added together. That includes `claims` and `recoveredMinor`: summing part-type rows
 * counts an order's claim, and what it recovered, once per part type.
 */
export interface FlagReportRowView {
  /** Truck, garage or part type id; null is the orders with no garage recorded. */
  key: string | null;
  label: string;
  currency: Currency;
  flaggedOrders: number;
  flaggedCostMinor: number;
  unflaggedOrders: number;
  unflaggedCostMinor: number;
  /** Cost by who bears it, for every order in the group, flagged or not. */
  costByBearer: BearerCostsView;
  claims: ClaimOutcomesView;
}

export const REPAIR_COST_SERIES = ['partType', 'truck'] as const;
export type RepairCostSeries = (typeof REPAIR_COST_SERIES)[number];

/**
 * `GET /repair-cost/trend` — `from` and `to` are months (`YYYY-MM`, inclusive); the default is the last twelve ending
 * this month. A `partType` series is one truck and one part type (parts cost); a `truck` series is one truck (labour
 * plus parts). A span over 24 months is refused.
 */
export const repairCostTrendQuerySchema = z.object({
  series: z.enum(REPAIR_COST_SERIES).default('partType'),
  from: month.optional(),
  to: month.optional(),
  truckId: z.uuid().optional(),
  partTypeId: z.uuid().optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
});
export type RepairCostTrendQuery = z.infer<typeof repairCostTrendQuerySchema>;

export interface TrendPointView {
  month: string;
  costMinor: number;
  orders: number;
  /** A repeat-flagged order falls in this month (for a part-type series, repeating that part type). */
  hasRepeatRepair: boolean;
}

export interface TrendSeriesView {
  truckId: string;
  truckLabel: string;
  partTypeId: string | null;
  partTypeName: string | null;
  currency: Currency;
  totalCostMinor: number;
  /** Every month in the range, empty ones as zero. */
  points: TrendPointView[];
}

export interface RepairCostTrendView {
  months: string[];
  series: TrendSeriesView[];
  total: number;
  page: number;
  pageSize: number;
}
